/**
 * UnifiedWebPlacementFlow Component
 *
 * Orchestrates the 3-step flow for deploying web placements from onboarding canvas:
 * 1. Select Web Placement (WebPlacementSelectorDialog) - choose existing or create new
 * 2. Configure Web Placement (WebPlacementDialog) - with pre-filled onboarding
 * 3. Deploy (DeployWebPlacementDialog) - publish to GCS
 *
 * @example
 * ```tsx
 * // From Onboarding Canvas
 * <UnifiedWebPlacementFlow
 *   open={true}
 *   onClose={() => setOpen(false)}
 *   projectId="proj-123"
 *   userId="user@example.com"
 *   source={{
 *     onboardingId: 'onb-1',
 *     versionId: 'ver-1',
 *     onboardingName: 'My Onboarding',
 *     versionName: 'Version 1',
 *   }}
 * />
 * ```
 *
 * @module webplacements/components
 */

import React, { useState, useCallback, useMemo } from 'react';
import { WebPlacementSelectorDialog, type WebPlacementSelectorResult } from './WebPlacementSelectorDialog';
import { WebPlacementDialog } from './WebPlacementDialog';
import { DeployWebPlacementDialog } from './DeployWebPlacementDialog';
import { WebPlacementService } from '@modules/firebase-core/services/WebPlacementService';
import { useWebPlacementDeploy } from '../hooks/useWebPlacementDeploy';
import type { WebPlacement } from '@modules/webplacements/types/webplacements.types';
import type { OnboardingSelection } from '@modules/placements/components/OnboardingPicker';

/**
 * Flow step type
 */
type FlowStep = 'select' | 'configure' | 'deploy';

/**
 * Configure mode
 */
type ConfigureMode = 'create' | 'edit';

/**
 * Source entity from onboarding canvas
 */
export interface WebPlacementSource {
  onboardingId: string;
  versionId: string;
  onboardingName: string;
  versionName?: string;
}

/**
 * Props for UnifiedWebPlacementFlow
 */
export interface UnifiedWebPlacementFlowProps {
  /** Whether the flow is open */
  open: boolean;
  /** Callback when flow is closed */
  onClose: () => void;
  /** Project ID */
  projectId: string;
  /** User ID/email for createdBy field */
  userId: string;
  /** Source onboarding from canvas */
  source: WebPlacementSource;
  /** Fired once a deploy/publish succeeds (Spec A / D51 — post-publish version prompt). */
  onPublished?: () => void;
  /** Class-P/C finding actions from the publish gate (react-router `navigate` from the host); passed through to `DeployWebPlacementDialog`. */
  onNavigate?: (path: string) => void;
}

/**
 * UnifiedWebPlacementFlow Component
 */
export const UnifiedWebPlacementFlow: React.FC<UnifiedWebPlacementFlowProps> = ({
  open,
  onClose,
  projectId,
  userId,
  source,
  onPublished,
  onNavigate,
}) => {
  // State
  const [step, setStep] = useState<FlowStep>('select');
  const [configureMode, setConfigureMode] = useState<ConfigureMode>('create');
  const [selectedWebPlacement, setSelectedWebPlacement] = useState<WebPlacement | null>(null);
  const [savedWebPlacement, setSavedWebPlacement] = useState<WebPlacement | null>(null);
  const [templateWebPlacement, setTemplateWebPlacement] = useState<WebPlacement | null>(null);
  // DC-21: "Fix first" / attribution finding highlights the toggles when back on step 2.
  const [highlightAttribution, setHighlightAttribution] = useState(false);

  // Services
  const webPlacementService = useMemo(() => new WebPlacementService(), []);

  // Deploy hook
  const { deploy, isDeploying } = useWebPlacementDeploy();

  // Prepare OnboardingSelection from source
  const initialOnboardingSelection: OnboardingSelection = useMemo(
    () => ({
      onboardingId: source.onboardingId,
      versionId: source.versionId,
      onboardingName: source.onboardingName,
      versionName: source.versionName || '',
      versionCreatedAt: new Date().toISOString(),
    }),
    [source]
  );

  // Reset state when dialog closes
  React.useEffect(() => {
    if (!open) {
      setStep('select');
      setConfigureMode('create');
      setSelectedWebPlacement(null);
      setSavedWebPlacement(null);
      setTemplateWebPlacement(null);
      setHighlightAttribution(false);
    }
  }, [open]);

  /**
   * Step 1: Handle duplicate from selector — open create mode with template + canvas onboarding
   */
  const handleSelectorDuplicate = useCallback((sourceWp: WebPlacement) => {
    setTemplateWebPlacement(sourceWp);
    setSelectedWebPlacement(null);
    setConfigureMode('create');
    setStep('configure');
  }, []);

  /**
   * Step 1: Handle full copy from selector — preserve source funnel and variable refs.
   */
  const handleSelectorFullCopy = useCallback(async (sourceWp: WebPlacement) => {
    try {
      const cloned = await webPlacementService.cloneFull(projectId, sourceWp.id, userId);
      setTemplateWebPlacement(null);
      setSelectedWebPlacement(cloned);
      setSavedWebPlacement(cloned);
      setConfigureMode('edit');
      setStep('configure');
    } catch (error) {
      console.error('Failed to full-copy web placement:', error);
    }
  }, [projectId, userId, webPlacementService]);

  /**
   * Step 1: Handle web placement selection
   */
  const handleSelect = useCallback((selection: WebPlacementSelectorResult) => {
    if (selection.type === 'create-new') {
      setConfigureMode('create');
      setSelectedWebPlacement(null);
    } else {
      setConfigureMode('edit');
      setSelectedWebPlacement(selection.webPlacement);
    }
    setStep('configure');
  }, []);

  /**
   * Step 2: Handle web placement create
   */
  const handleCreate = useCallback(
    async (data: Omit<Parameters<typeof webPlacementService.create>[1], 'createdBy'>): Promise<WebPlacement> => {
      const created = await webPlacementService.create(projectId, {
        ...data,
        createdBy: userId,
      });
      return created;
    },
    [projectId, userId, webPlacementService]
  );

  /**
   * Step 2: Handle web placement update
   */
  const handleUpdate = useCallback(
    async (id: string, data: Parameters<typeof webPlacementService.update>[2]): Promise<WebPlacement> => {
      const updated = await webPlacementService.update(projectId, id, data);
      return updated;
    },
    [projectId, webPlacementService]
  );

  /**
   * Step 2: Handle deploy request from WebPlacementDialog
   */
  const handleDeployRequested = useCallback((webPlacement: WebPlacement) => {
    setSavedWebPlacement(webPlacement);
    setHighlightAttribution(false);
    setStep('deploy');
  }, []);

  /** DC-21: "Fix first" / attribution finding → back to step 2 on the SAME placement, toggles highlighted. */
  const handleBackToConfigure = useCallback(() => {
    if (!savedWebPlacement) return;
    setSelectedWebPlacement(savedWebPlacement);
    setConfigureMode('edit');
    setHighlightAttribution(true);
    setStep('configure');
  }, [savedWebPlacement]);

  /**
   * Step 3: Handle deploy
   */
  const handleDeploy = useCallback(
    async (webPlacementId: string) => {
      const result = await deploy({
        projectId,
        webPlacementId,
        userId,
      });
      // Publishing freezes the shipped version — offer the post-publish version
      // prompt only once the deploy actually succeeded (Spec A / D51, A4-2).
      if (result?.success) {
        onPublished?.();
      }
      return result;
    },
    [deploy, projectId, userId, onPublished]
  );

  /**
   * Check alias uniqueness
   */
  const isAliasUnique = useCallback(
    async (alias: string, excludeId?: string) => {
      return webPlacementService.isAliasUnique(projectId, alias, excludeId);
    },
    [projectId, webPlacementService]
  );

  // Don't render anything if not open
  if (!open) {
    return null;
  }

  return (
    <>
      {/* Step 1: Select Web Placement */}
      {step === 'select' && (
        <WebPlacementSelectorDialog
          open={true}
          onClose={onClose}
          projectId={projectId}
          onboardingId={source.onboardingId}
          onSelect={handleSelect}
          onDuplicate={handleSelectorDuplicate}
          onFullCopy={handleSelectorFullCopy}
        />
      )}

      {/* Step 2: Configure Web Placement */}
      {step === 'configure' && (
        <WebPlacementDialog
          open={true}
          onClose={onClose}
          mode={configureMode}
          projectId={projectId}
          userId={userId}
          webPlacement={selectedWebPlacement || undefined}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
          onDeployRequested={handleDeployRequested}
          isAliasUnique={isAliasUnique}
          initialOnboardingSelection={initialOnboardingSelection}
          lockOnboarding={true}
          templateWebPlacement={templateWebPlacement || undefined}
          highlightAttribution={highlightAttribution}
        />
      )}

      {/* Step 3: Deploy Web Placement */}
      {step === 'deploy' && savedWebPlacement && (
        <DeployWebPlacementDialog
          open={true}
          onClose={onClose}
          webPlacement={savedWebPlacement}
          onDeploy={handleDeploy}
          isDeploying={isDeploying}
          projectId={projectId}
          onBackToConfigure={handleBackToConfigure}
          onNavigate={onNavigate}
        />
      )}
    </>
  );
};

export default UnifiedWebPlacementFlow;
